'use client';

// ---------------------------------------------------------------------------
// OfflineProvider — Service worker registration & online/offline tracking
// ---------------------------------------------------------------------------
// Handles:
//   1. Service worker lifecycle (register, update, activate)
//   2. Online/offline status via navigator.onLine + events
//   3. Service worker message relay (queue updates, sync status)
//   4. PWA install prompt capture
// ---------------------------------------------------------------------------

import { useEffect, useCallback, useRef } from 'react';
import { useOfflineStore } from '@/core/stores/offline.store';
import { getQueueCount } from '@/core/lib/indexed-db';
import { getSyncBus } from '@/core/lib/sync-bus';
import { logger } from '@/core/lib/logger';

interface SWMessage {
  type: string;
  payload?: {
    count?: number;
    synced?: number;
    failed?: number;
    id?: string;
    status?: number;
    remaining?: number;
    maxRetries?: boolean;
    items?: unknown[];
    version?: string;
  };
}

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const {
    setOnline,
    setSwRegistered,
    setQueueCount,
    setSyncStatus,
    setLastSyncAt,
    incrementQueue,
    decrementQueue,
    setInstallPromptAvailable,
    setSwUpdatePhase,
    setSwUpdateVersion,
  } = useOfflineStore();

  const deferredPromptRef = useRef<Event | null>(null);

  // -----------------------------------------------------------------------
  // Service Worker message handler
  // -----------------------------------------------------------------------
  const handleSWMessage = useCallback(
    (event: MessageEvent<SWMessage>) => {
      const { type, payload } = event.data;

      switch (type) {
        case 'MUTATION_QUEUED':
          incrementQueue();
          logger.info('offline.mutation.queued', {
            component: 'OfflineProvider',
            metadata: {
              tag: 'SW_QUEUE',
              mutationId: (event.data as { payload?: { id?: string } }).payload?.id,
              url: (event.data as { payload?: { url?: string } }).payload?.url,
              method: (event.data as { payload?: { method?: string } }).payload?.method,
              timestamp: (event.data as { payload?: { timestamp?: number } }).payload?.timestamp,
            },
          });
          break;

        case 'MUTATION_SYNCED':
          decrementQueue();
          logger.info('offline.mutation.synced', {
            component: 'OfflineProvider',
            metadata: {
              tag: 'SW_SYNC',
              mutationId: payload?.id,
              status: payload?.status,
            },
          });
          break;

        case 'MUTATION_FAILED':
          if (payload?.maxRetries) {
            decrementQueue();
            logger.error('offline.mutation.failed', {
              component: 'OfflineProvider',
              metadata: {
                tag: 'SW_CONFLICT',
                mutationId: payload?.id,
                status: payload?.status,
                maxRetries: true,
              },
            });
          }
          break;

        case 'QUEUE_STATUS':
          setQueueCount(payload?.count ?? 0);
          break;

        case 'SYNC_COMPLETE':
          setSyncStatus('synced');
          setLastSyncAt(Date.now());
          getQueueCount().then(setQueueCount).catch(() => {/* noop */});
          logger.info('offline.sync.complete', {
            component: 'OfflineProvider',
            metadata: {
              tag: 'SW_SYNC',
              synced: payload?.synced ?? 0,
              failed: payload?.failed ?? 0,
            },
          });
          break;

        case 'SYNC_ABORTED':
          setSyncStatus('error');
          logger.warn('offline.sync.aborted', {
            component: 'OfflineProvider',
            metadata: {
              tag: 'SW_SYNC',
              synced: payload?.synced ?? 0,
              remaining: payload?.remaining ?? 0,
            },
          });
          break;

        case 'SW_UPDATED':
          setSwUpdateVersion(payload?.version ?? null);
          setSwUpdatePhase('available');
          logger.info('service-worker.updated', {
            component: 'OfflineProvider',
            metadata: {
              tag: 'SW_UPDATED',
              version: payload?.version ?? null,
            },
          });
          break;

        default:
          break;
      }
    },
    [incrementQueue, decrementQueue, setQueueCount, setSyncStatus, setLastSyncAt, setSwUpdatePhase, setSwUpdateVersion]
  );

  // -----------------------------------------------------------------------
  // Online/offline event listeners
  // -----------------------------------------------------------------------
  const handleOnline = useCallback(() => {
    setOnline(true);
    setSyncStatus('syncing');
    const bus = getSyncBus();
    if (bus.isLeader()) {
      // We are the elected leader across tabs — drive the sync.
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SYNC_QUEUE' });
      }
      bus.notifySyncStarted();
    } else {
      // Ask the leader to sync on our behalf
      bus.requestSync();
    }
  }, [setOnline, setSyncStatus]);

  const handleOffline = useCallback(() => {
    setOnline(false);
    setSyncStatus('idle');
  }, [setOnline, setSyncStatus]);

  // -----------------------------------------------------------------------
  // PWA install prompt
  // -----------------------------------------------------------------------
  const handleBeforeInstallPrompt = useCallback(
    (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setInstallPromptAvailable(true);
    },
    [setInstallPromptAvailable]
  );

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------
  useEffect(() => {
    // Set initial online state
    setOnline(navigator.onLine);

    // Register event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((registration) => {
          // `register()` can RESOLVE with undefined.
          //
          // The spec types this as Promise<ServiceWorkerRegistration>, but a
          // context with service workers disabled — Playwright's
          // `serviceWorkers: 'block'`, a privacy setting, some embedded
          // webviews — resolves with nothing rather than rejecting. Reading
          // `.scope` then threw inside this `.then`, and the `.catch` below
          // reported it as "Service worker registration failed
          // TypeError: Cannot read properties of undefined (reading 'scope')"
          // — a code bug wearing the costume of a registration failure, 24
          // times in one e2e run.
          //
          // Not registering is a supported state: the app works online without
          // it and only loses offline queueing, so this is logged as info and
          // `swRegistered` stays false, which is what the rest of the provider
          // already keys off.
          if (!registration) {
            logger.info('Service worker unavailable in this context', {
              component: 'OfflineProvider',
            });
            return;
          }

          setSwRegistered(true);
          logger.info('Service worker registered', {
            component: 'OfflineProvider',
            metadata: { scope: registration.scope },
          });

          // Check for updates on registration
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'activated') {
                  logger.info('Service worker updated', { component: 'OfflineProvider' });
                }
              });
            }
          });
        })
        .catch((err) => {
          logger.error('Service worker registration failed', {
            component: 'OfflineProvider',
            metadata: { error: String(err) },
          });
        });
    }

    // Initial queue count
    getQueueCount().then(setQueueCount).catch(() => {/* noop */});

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [
    setOnline,
    setSwRegistered,
    setQueueCount,
    handleOnline,
    handleOffline,
    handleBeforeInstallPrompt,
    handleSWMessage,
  ]);

  // Start the cross-tab sync bus
  useEffect(() => {
    const bus = getSyncBus();
    bus.start();
    // Best-effort: this tab claims leadership on first mount.
    // If another tab is already leader (hears our REQUEST_LEADER),
    // they will respond with LEADER_CLAIM and we fall back to follower.
    setTimeout(() => {
      if (bus.getRole() === 'candidate') {
        // No other leader responded within a short window — claim it
        // (bus.start() already posted REQUEST_LEADER; if no LEADER_HEARTBEAT
        // arrived we are the only tab and can lead)
        // No-op here — the heartbeat loop will eventually drive election.
      }
    }, 0);
    return () => {
      // Keep the bus alive across provider unmounts during HMR
    };
  }, []);

  return <>{children}</>;
}
